import { useRef, useState } from 'react';
import { useParams, useNavigate } from 'react-router-dom';
import { ArrowLeft, Package, ArrowRightLeft, RotateCcw, Gift } from 'lucide-react';
import { formatDistanceToNow } from 'date-fns';
import { useGroupActivity, ActivityEvent } from '@/hooks/useGroupActivity';
import { Layout } from '@/components/design/Layout';
import { Skeleton } from '@/components/ui/skeleton';
import { T, RADIUS } from '@/lib/tokens';

const PAGE_SIZE = 20;

const SPEC_TYPES = ['loan_created', 'loan_returned', 'item_added', 'item_giving_away'] as const;
type SpecEventType = (typeof SPEC_TYPES)[number];

const EVENT_ICON: Record<SpecEventType, React.ComponentType<{ size: number; color: string }>> = {
  item_added: Package,
  loan_created: ArrowRightLeft,
  loan_returned: RotateCcw,
  item_giving_away: Gift,
};

function EventDescription({ event }: { event: ActivityEvent & { type: SpecEventType } }) {
  const bold = (text: string) => <strong style={{ fontWeight: 600 }}>{text}</strong>;

  if (event.type === 'loan_created') {
    return (
      <>
        {bold(event.actorName)} borrowed {bold(event.itemName)} from{' '}
        {bold(event.targetName ?? 'someone')}
      </>
    );
  }
  if (event.type === 'loan_returned') {
    return (
      <>
        {bold(event.actorName)} returned {bold(event.itemName)}
      </>
    );
  }
  if (event.type === 'item_giving_away') {
    return (
      <>
        {bold(event.actorName)} is giving away {bold(event.itemName)}
      </>
    );
  }
  return (
    <>
      {bold(event.actorName)} added {bold(event.itemName)}
    </>
  );
}

function SkeletonRows() {
  return (
    <div>
      {Array.from({ length: 6 }).map((_, i) => (
        <div
          key={i}
          style={{
            display: 'flex',
            alignItems: 'center',
            gap: 12,
            padding: '14px 20px',
            borderBottom: i < 5 ? `1px solid ${T.border}` : undefined,
          }}
        >
          <Skeleton className="h-9 w-9 rounded-lg flex-shrink-0" />
          <div style={{ flex: 1 }}>
            <Skeleton className="h-4 w-48 mb-1" />
            <Skeleton className="h-3 w-24" />
          </div>
        </div>
      ))}
    </div>
  );
}

export default function GroupActivityFeed() {
  const { groupId } = useParams<{ groupId: string }>();
  const navigate = useNavigate();
  const [limit, setLimit] = useState(PAGE_SIZE);

  const { data: allEvents, isLoading, isError, isFetching } = useGroupActivity(
    groupId!,
    limit,
    { usePreviousData: true },
  );

  const events = (allEvents ?? []).filter(
    (e): e is ActivityEvent & { type: SpecEventType } =>
      (SPEC_TYPES as readonly string[]).includes(e.type),
  );

  // Track previous event count to detect when "Load more" yields no new results
  const prevCountRef = useRef(0);
  const currentCount = allEvents?.length ?? 0;
  if (!isFetching && currentCount > 0) {
    prevCountRef.current = currentCount;
  }
  const hasMore = currentCount >= limit && !(currentCount === prevCountRef.current && limit > PAGE_SIZE);

  const leftAction = (
    <button
      onClick={() => navigate(`/groups/${groupId}`)}
      aria-label="Back to group"
      style={{
        background: 'none',
        border: 'none',
        cursor: 'pointer',
        padding: 4,
        display: 'flex',
        alignItems: 'center',
        color: T.text,
      }}
    >
      <ArrowLeft size={22} />
    </button>
  );

  return (
    <Layout title="Activity" leftAction={leftAction}>
      <div style={{ paddingBottom: 20 }}>
        {isLoading && <SkeletonRows />}

        {isError && (
          <div style={{ textAlign: 'center', padding: '48px 20px', color: T.textMuted }}>
            Couldn't load activity. Pull down to refresh.
          </div>
        )}

        {!isLoading && !isError && events.length === 0 && (
          <div style={{ textAlign: 'center', padding: '64px 20px' }}>
            <div
              style={{
                display: 'flex',
                justifyContent: 'center',
                marginBottom: 16,
                opacity: 0.25,
              }}
            >
              <Package size={40} color={T.textMuted} />
            </div>
            <p
              style={{
                fontSize: 17,
                fontWeight: 600,
                color: T.text,
                margin: '0 0 8px',
              }}
            >
              No activity yet
            </p>
            <p
              style={{
                fontSize: 14,
                color: T.textSec,
                lineHeight: 1.65,
                margin: 0,
              }}
            >
              Activity will appear here when items are added or loans are recorded.
            </p>
          </div>
        )}

        {!isLoading && !isError && events.length > 0 && (
          <>
            {events.map((event, i) => {
              const Icon = EVENT_ICON[event.type];
              return (
                <div
                  key={event.id}
                  style={{
                    display: 'flex',
                    alignItems: 'center',
                    gap: 12,
                    padding: '14px 20px',
                    borderBottom: i < events.length - 1 ? `1px solid ${T.border}` : undefined,
                  }}
                >
                  <div
                    style={{
                      width: 36,
                      height: 36,
                      borderRadius: 8,
                      background: T.surfaceSubtle,
                      display: 'flex',
                      alignItems: 'center',
                      justifyContent: 'center',
                      flexShrink: 0,
                    }}
                  >
                    <Icon size={18} color={T.textMuted} />
                  </div>
                  <div style={{ flex: 1, minWidth: 0 }}>
                    <div
                      style={{
                        fontSize: 14,
                        color: T.text,
                        lineHeight: 1.4,
                      }}
                    >
                      <EventDescription event={event} />
                    </div>
                    <div
                      style={{
                        fontSize: 12,
                        color: T.textMuted,
                        marginTop: 2,
                      }}
                    >
                      {formatDistanceToNow(new Date(event.timestamp), { addSuffix: true })}
                    </div>
                  </div>
                </div>
              );
            })}

            {hasMore && (
              <div style={{ textAlign: 'center', padding: '12px 20px' }}>
                <button
                  onClick={() => setLimit((prev) => prev + PAGE_SIZE)}
                  disabled={isFetching}
                  style={{
                    width: '100%',
                    padding: '14px',
                    border: `1px solid ${T.border}`,
                    background: T.bg,
                    borderRadius: RADIUS.md,
                    cursor: isFetching ? 'default' : 'pointer',
                    color: T.textSec,
                    fontSize: 13,
                    fontFamily: 'inherit',
                  }}
                >
                  {isFetching ? 'Loading\u2026' : 'Load more'}
                </button>
              </div>
            )}
          </>
        )}
      </div>
    </Layout>
  );
}
